import * as React from 'react';
import { ThunkDispatch } from 'redux-thunk';
import i18next from 'i18next';
import { AnyAction } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Formik, Form, FormikHelpers, Field } from 'formik';

import { AppState } from '../../../store';
import { FormField } from '../../../components/FormField/FormField';
import { Authentication, SendOTPFormPayload, VerifyOTPFormPayload, SignupFormPayload } from '../../../models/authentication';
import { HTTPStatusCode, Type } from '../../../helpers/types';
import { UserContainer, BackgroundImage, UserForm, Title, SubTitle, FormFields, FormFooterNote, FormFooterLink } from '../../../styles/styled-components/User/User';
import { RouteComponentProps } from 'react-router';
import { getCurrentAppLang } from '../../../helpers/util';
import { actionSendOTP, actionVerifyOTP, actionSignUp } from '../../../store/authentication/action';
import { OTP_RESEND_TIME_INTERVAL } from '../../../constants';
import FormError from '../../../components/FormError/FormError';
import { SubmitButton } from '../../../components/SubmitButton/SubmitButton';
import { colors } from '../../../styles/colors';

interface OTPVerificationProps extends RouteComponentProps<any> {
  sendOTP: (payload: SendOTPFormPayload) => void;
  verifyOTP: (payload: VerifyOTPFormPayload) => void;
  signUp: (payload: SignupFormPayload) => void;
  authentication: Authentication;
}

interface VerificationViewStates {
  otp1: string;
  otp2: string;
  otp3: string;
  otp4: string;
  password: string;
  seconds: number;
  loader: boolean;
  error?: string;
}

class OTPVerification extends React.Component<OTPVerificationProps, VerificationViewStates> {

  timer: any;
  otp1Ref: { current: null | HTMLDivElement };
  otp2Ref: { current: null | HTMLDivElement };
  otp3Ref: { current: null | HTMLDivElement };
  otp4Ref: { current: null | HTMLDivElement };

  state: VerificationViewStates = {
    otp1: '',
    otp2: '',
    otp3: '',
    otp4: '',
    password: '',
    seconds: OTP_RESEND_TIME_INTERVAL,
    loader: false,
  };

  constructor(props: OTPVerificationProps) {
    super(props);
    this.otp1Ref = React.createRef();
    this.otp2Ref = React.createRef();
    this.otp3Ref = React.createRef();
    this.otp4Ref = React.createRef();
    this.tick = this.tick.bind(this);
  }

  componentDidMount() {
    const type = this.getType();
    if (type === 'signup') {
      if (!this.props.authentication.signUpPassword) {
        this.props.history.push(`signup`);
      }
    }
    this.timer = setInterval(this.tick, 1000);
  }

  getType(): string {
    const search = this.getUrlParams();
    return search.get('type') || '';
  }

  getUrlParams(): URLSearchParams {
    if (!this.props.location.search) return new URLSearchParams();
    return new URLSearchParams(this.props.location.search);
  }

  tick() {
    if (this.state.seconds > 0) {
      this.setState({ seconds: this.state.seconds - 1 });
    } else {
      clearInterval(this.timer);
      this.timer = setInterval(this.tick, 1000);
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  getUsername(): string {
    const search = this.getUrlParams();
    return decodeURIComponent(search.get('username') as string) || '';
  }

  async handleResendOtp(setStatus: any) {
    if (this.state.seconds === 0) {
      const language = getCurrentAppLang(true).split('_')[1];
      await this.props.sendOTP({ username: this.getUsername(), password: this.props.authentication.signUpPassword, type: Type.EMAIL, lang: language });
      if (this.props.authentication.error && this.props.authentication.error.status) {
        setStatus({ error: (this.props.authentication.error.status === HTTPStatusCode.TooManyRequest) ? 'verification.error.max' : 'form.generic.error' });
      } else {
        this.setState({ seconds: OTP_RESEND_TIME_INTERVAL });
      }
    }
    return false;
  }

  handleTextChange(e: React.ChangeEvent<HTMLInputElement>, targetPosition: number) {
    const _this: any = this;
    const focusNextTarget = `otp${(targetPosition === 4) ? targetPosition : + targetPosition + 1}Ref`;
    const focusPrevTarget = `otp${(targetPosition === 1) ? targetPosition : targetPosition - 1}Ref`;
    const value = e.target.value;
    if (value) {
      _this[focusNextTarget].current.focus();
    } else {
      _this[focusPrevTarget].current.focus();
    }
    return;
  }

  async handleSubmit(values: VerificationViewStates, actions: FormikHelpers<VerificationViewStates>) {
    if (!(values.otp1 && values.otp2 && values.otp3 && values.otp4)) {
      actions.setStatus({ error: 'verification.error.empty' });
    } else {
      this.setState({ loader: true });
      const username = this.getUsername();
      const password = `${values.otp1}${values.otp2}${values.otp3}${values.otp4}`;
      const type = this.getType();
      actions.setFieldValue('password', password);
      await this.props.verifyOTP({ username: username, password: password, type: Type.EMAIL });
      if (this.props.authentication.error && this.props.authentication.error.status) {
        const status = this.props.authentication.error.status;
        let errMsg = 'form.generic.error';
        if (status === HTTPStatusCode.TooManyRequest) {
          errMsg = 'verification.error.maxResendOtp';
        } else if (status === HTTPStatusCode.Unauthorized) {
          errMsg = 'verification.error.match';
        }
        actions.setStatus({ error: errMsg });
        this.setState({ loader: false });
      } else {
        if (type === 'signup') {
          if (this.props.authentication.verifiedOTP) {
            await this.props.signUp({
              customer: {
                name: { nickName: username },
              },
              password: this.props.authentication.signUpPassword,
              otpVerifiedToken: this.props.authentication.verifiedOTPToken,
            });
            if (this.props.authentication.error) {
              actions.setStatus({ error: 'form.email.error.match' });
            }
            this.setState({ loader: false });
          }
        } else {
          this.props.history.push(`change-password`);
        }
      }
    }
  }

  render() {

    return (

      <UserContainer>
        <BackgroundImage />
        <UserForm>
          <Formik
            initialValues={this.state}
            onSubmit={(values, actions) => this.handleSubmit(values, actions)}>

            {({ values, status, setStatus }) => (
              <Form method="POST">
                <Title>{i18next.t('verification.title')}</Title>
                <SubTitle>{i18next.t('verification.text', { email: this.getUsername() })}</SubTitle>
                <FormError name="OTPVerification" data={status} />

                <InlineField>
                  <FormField name="otp1">
                    <Field name="otp1" value={values.otp1}>
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          type="number"
                          ref={this.otp1Ref} maxLength={1}
                          onChange={e => {
                            this.handleTextChange(e, 1);
                            form.setFieldValue('otp1', e.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </FormField>

                  <FormField name="otp2">
                    <Field name="otp2" value={values.otp1}>
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          type="number"
                          ref={this.otp2Ref} maxLength={1}
                          onChange={e => {
                            this.handleTextChange(e, 2);
                            form.setFieldValue('otp2', e.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </FormField>

                  <FormField name="otp3">
                    <Field name="otp3" value={values.otp1}>
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          type="number"
                          ref={this.otp3Ref} maxLength={1}
                          onChange={e => {
                            this.handleTextChange(e, 3);
                            form.setFieldValue('otp3', e.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </FormField>

                  <FormField name="otp4" >
                    <Field name="otp4" value={values.otp1}>
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          type="number"
                          ref={this.otp4Ref} maxLength={1}
                          onChange={e => {
                            this.handleTextChange(e, 4);
                            form.setFieldValue('otp4', e.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </FormField>

                </InlineField>

                <SubmitButton>
                  {i18next.t('verification.continueButton')}
                </SubmitButton>

                <FormFooterNote>{i18next.t('verification.notReceived')} &nbsp; </FormFooterNote>

                <FormFooterLink style={{ marginTop: 0 }}>
                  {(this.state.seconds === 0) ?
                    <span onClick={() => this.handleResendOtp(setStatus)}>
                      {i18next.t('verification.resend')}
                    </span>
                    :
                    <React.Fragment>
                      {i18next.t('verification.resendIn')} {this.state.seconds} {i18next.t('verification.seconds')}
                    </React.Fragment>
                  }
                </FormFooterLink>

              </Form>
            )}
          </Formik>
        </UserForm>
      </UserContainer>
    );
  }
}

const InlineField = styled(FormFields)`
    display: flex;
    margin: auto;
    line-height: 2.7 !important;
    color: ${colors.lightTextColor};
    font-size: 1.750em;
    text-align: center;
    width: 85%;

    .field {
        margin: 0 0.5em;
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
        width: 85%;
        font-size: 1em;
        text-align: center;
        padding-right: 1em;
    }
`;

const mapStateToProps = (state: AppState, ownProps: OTPVerificationProps) => {
  return {
    authentication: state.authentication,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    sendOTP: (payload: SendOTPFormPayload) => dispatch(actionSendOTP(payload)),
    verifyOTP: (payload: VerifyOTPFormPayload) => dispatch(actionVerifyOTP(payload)),
    signUp: (payload: SignupFormPayload) => dispatch(actionSignUp(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OTPVerification);